import Modal from "@/components/shared/Modal.tsx";
import { FormProvider, useForm } from "react-hook-form";
import TextInput from "@/components/shared/form/fields/TextInput.tsx";
import FormActions from "@/components/shared/form/fields/FormActions.tsx";
import PageLoading from "@/components/shared/PageLoading.tsx";
import { isEmpty } from "lodash";
import { useBatchRenewInvites, useInvite } from "./api";

type BatchRenewInviteModalArgs = {
  inviteId: string;
  inviteIds: string[];
  setShowModal: (showModal: boolean) => void;
  onSuccess?: () => void;
};

export default function BatchRenewInviteModal({
  inviteId,
  inviteIds,
  setShowModal,
  onSuccess,
}: BatchRenewInviteModalArgs) {
  const { data: invite, isLoading } = useInvite(inviteId);
  const { mutate: batchRenew } = useBatchRenewInvites({
    onSuccess: () => {
      setShowModal(false);
      toastr.success(I18n.t("js.invites.batch_renew.success_flash"));
      onSuccess?.();
    },
    onError: () => {
      toastr.error(I18n.t("js.invites.batch_renew.not_renewable_flash"));
    },
  });
  const methods = useForm<{ body: string }>({
    values: { body: invite?.body || "" },
  });

  function submit(data: { body: string }) {
    if (isEmpty(inviteIds)) {
      toastr.error(I18n.t("js.invites.batch_renew.empty_error_flash"));
      return;
    }

    if (invite?.type === "ExternalInvite" && !data.body.match(/\$LINK/)) {
      toastr.error(I18n.t("js.invites.validation.link_in_body"));
      return;
    }

    batchRenew({ body: data.body, invite_ids: inviteIds });
  }

  return (
    <Modal
      title={I18n.t("js.invites.actions.renew_invite")}
      onClose={() => setShowModal(false)}
    >
      {isLoading ? (
        <PageLoading />
      ) : (
        <FormProvider {...methods}>
          <form
            className="flex flex-col gap-5 min-h-[368px]"
            onSubmit={methods.handleSubmit(submit)}
          >
            <TextInput
              name="body"
              label={I18n.t(
                `js.invites.renew.${(invite?.type || "externalinvite").toLowerCase()}.body_label`,
              )}
              rows={10}
              multiline
            />
            <FormActions
              saveText={I18n.t("js.invites.renew.submit")}
              onCancel={() => setShowModal(false)}
            />
          </form>
        </FormProvider>
      )}
    </Modal>
  );
}
